/* haven-reels – scroll-snap video strip with arrows, autoplay and drag (no Swiper dependency) */
if (!customElements.get('haven-reels')) {
  class HavenReels extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.haven-reels__track');
      if (!this.track) return;

      this.prevButton = this.querySelector('.haven-reels__arrow--prev');
      this.nextButton = this.querySelector('.haven-reels__arrow--next');
      this.toggleButton = this.querySelector('.haven-reels__toggle');
      this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reducedMotion;
      this.userPaused = false;
      this.editorPaused = false;

      if (this.toggleButton) this.toggleButton.addEventListener('click', () => this.togglePlayback());
      this.lazyLoadFrames();
      this.autoplaySpeed = Math.max(2, parseFloat(this.dataset.autoplaySpeed) || 6) * 1000;

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.scrollByStep(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.scrollByStep(1));

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      // Drag to scroll with a mouse (touch already scrolls natively).
      this.track.addEventListener('pointerdown', this.onPointerDown);

      // Pause while hovering / focused, resume after.
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());

      // Only autoplay while on screen.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onVisibility, { threshold: 0.25 });
        this.observer.observe(this);
      } else {
        this.startAutoplay();
      }

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      this.updateButtons();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.onResize);
      if (this.observer) this.observer.disconnect();
      if (this.frameObserver) this.frameObserver.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    get slides() {
      return Array.from(this.track.children);
    }

    stepSize() {
      const first = this.slides[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth - 1);
    }

    scrollByStep(direction) {
      const step = this.stepSize();
      if (!step) return;
      this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    scrollToSlide(slide) {
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }

    updateButtons() {
      const left = this.track.scrollLeft;
      if (this.prevButton) this.prevButton.disabled = left <= 1;
      if (this.nextButton) this.nextButton.disabled = left >= this.maxScroll();
    }

    onScroll() {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.updateButtons(), 60);
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.updateButtons(), 120);
    }

    onVisibility(entries) {
      entries.forEach((entry) => {
        this.isVisible = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      });
    }

    /* ---- lazy embeds + pause control ---------------------------------- */

    lazyLoadFrames() {
      const frames = Array.from(this.querySelectorAll('iframe[data-src]'));
      if (!frames.length) return;
      const load = (frame) => {
        if (!frame.dataset.src) return;
        frame.src = frame.dataset.src;
        frame.removeAttribute('data-src');
      };
      if (!('IntersectionObserver' in window)) {
        frames.forEach(load);
        return;
      }
      this.frameObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            load(entry.target);
            this.frameObserver.unobserve(entry.target);
            if (this.userPaused) entry.target.addEventListener('load', () => this.postToFrame(entry.target, false), { once: true });
          });
        },
        { root: null, rootMargin: '200px' }
      );
      frames.forEach((frame) => this.frameObserver.observe(frame));
    }

    postToFrame(frame, play) {
      if (!frame.contentWindow || !frame.src) return;
      if (frame.src.includes('vimeo.com')) {
        frame.contentWindow.postMessage(JSON.stringify({ method: play ? 'play' : 'pause' }), '*');
      } else if (frame.src.includes('youtube.com')) {
        frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: play ? 'playVideo' : 'pauseVideo', args: [] }), '*');
      }
    }

    togglePlayback() {
      this.userPaused = !this.userPaused;
      const btn = this.toggleButton;
      btn.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      btn.setAttribute('aria-label', this.userPaused ? btn.dataset.labelPlay : btn.dataset.labelPause);
      btn.classList.toggle('is-paused', this.userPaused);
      this.querySelectorAll('video').forEach((video) => (this.userPaused ? video.pause() : video.play().catch(() => {})));
      this.querySelectorAll('iframe').forEach((frame) => this.postToFrame(frame, !this.userPaused));
      this.userPaused ? this.stopAutoplay() : this.startAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.userPaused || this.editorPaused || this.autoplayTimer || this.isVisible === false || this.slides.length < 2) return;
      this.autoplayTimer = setInterval(() => {
        if (this.track.scrollLeft >= this.maxScroll()) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.scrollByStep(1);
        }
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    /* ---- mouse drag ---------------------------------------------------- */

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;
      this.dragMoved = false;
      this.track.classList.add('is-dragging');
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp, { once: true });
      window.addEventListener('pointercancel', this.onPointerUp, { once: true });
    }

    onPointerMove(event) {
      const delta = event.clientX - this.dragStartX;
      if (Math.abs(delta) > 3) this.dragMoved = true;
      this.track.scrollLeft = this.dragStartScroll - delta;
    }

    onPointerUp(event) {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      if (event && event.type === 'pointercancel') {
        this.track.classList.remove('is-dragging');
        return;
      }
      this.track.classList.remove('is-dragging');
      if (this.dragMoved) {
        // Snap to the nearest slide after a drag.
        const step = this.stepSize();
        if (step) {
          const index = Math.round(this.track.scrollLeft / step);
          this.track.scrollTo({ left: index * step, behavior: 'smooth' });
        }
        // Swallow the click that follows a drag so links inside slides don't fire.
        const swallow = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        this.track.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => this.track.removeEventListener('click', swallow, { capture: true }), 0);
      }
    }

    /* ---- theme editor -------------------------------------------------- */

    onBlockSelect(event) {
      const slide = event.target && event.target.closest ? event.target.closest('.haven-reels__slide') : null;
      if (!slide || !this.contains(slide)) return;
      this.editorPaused = true;
      this.stopAutoplay();
      this.scrollToSlide(slide);
    }

    onBlockDeselect(event) {
      const slide = event.target && event.target.closest ? event.target.closest('.haven-reels__slide') : null;
      if (!slide || !this.contains(slide)) return;
      this.editorPaused = false;
      this.startAutoplay();
    }
  }

  customElements.define('haven-reels', HavenReels);
}
